import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Segmented,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { createChangeOrder, getMeta, listChangeOrders } from "./api";
import { formatDay, formatEuro } from "../workReports/format";
import ContractOverview from "./ContractOverview";
import { STATE_COLORS, reasonLabels, stateLabels } from "./labels";
import "./changeOrders.css";

const NewChangeOrderModal = ({ open, meta, initialOffer, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const withOffer = Form.useWatch("withOffer", form);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        withOffer: meta?.offers?.length ? "yes" : "no",
        parentOffer: initialOffer || undefined,
        client: undefined,
        project: undefined,
        title: "",
        reason: "additional_work"
      });
    }
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await createChangeOrder({
        title: values.title,
        reason: values.reason,
        ...(values.withOffer === "yes"
          ? { parentOffer: values.parentOffer }
          : { client: values.client, project: values.project || null })
      });
      history.push(`/user/change-orders/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="change-orders-page"
      open={open}
      title={t("New change order")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="withOffer">
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value="yes">{t("To an accepted offer")}</Radio.Button>
            <Radio.Button value="no">{t("Without an offer")}</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {withOffer === "yes" ? (
          <Form.Item
            name="parentOffer"
            label={t("Main offer")}
            rules={[{ required: true, message: t("Please choose the offer.") }]}
            extra={!meta?.offers?.length ? t("There is no accepted offer yet.") : null}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={(meta?.offers || []).map(o => ({
                value: o._id,
                label: [o.label, o.title, o.clientName].filter(Boolean).join(" · ")
              }))}
            />
          </Form.Item>
        ) : (
          <>
            <Form.Item
              name="client"
              label={t("Customer")}
              rules={[{ required: true, message: t("Please choose the customer.") }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={(meta?.clients || []).map(c => ({ value: c._id, label: c.name }))}
              />
            </Form.Item>
            <Form.Item name="project" label={t("Project")}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                options={(meta?.projects || []).map(p => ({ value: p._id, label: p.name }))}
              />
            </Form.Item>
          </>
        )}
        <Form.Item
          name="title"
          label={t("Title")}
          rules={[{ required: true, message: t("Please give the change order a title.") }]}
        >
          <Input maxLength={200} placeholder={t("e.g. Additional sealing of the shower wall")} />
        </Form.Item>
        <Form.Item name="reason" label={t("Reason")}>
          <Select
            options={Object.entries(reasonLabels(t)).map(([value, label]) => ({ value, label }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ChangeOrders = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const initialOffer = new URLSearchParams(location.search).get("offer") || "";
  const [meta, setMeta] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState("all");
  const [parentOffer, setParentOffer] = useState(initialOffer);
  const [data, setData] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const states = stateLabels(t);

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
  }, []);

  useEffect(() => {
    setLoading(true);
    listChangeOrders({
      searchText,
      state: ["pending", "approved", "draft"].includes(filter) ? filter : undefined,
      overdue: filter === "overdue",
      parentOffer: parentOffer || undefined
    })
      .then(res => {
        setData(res.data.data);
        setOverview(res.data.overview);
      })
      .catch(generalErrorAlert)
      .finally(() => setLoading(false));
  }, [searchText, filter, parentOffer]);

  const columns = [
    {
      title: t("Change order"),
      key: "number",
      mobile: "title",
      render: (_, item) => (
        <div>
          <Link to={`/user/change-orders/${item._id}`}>
            <Typography.Text strong>
              {item.number} {item.title}
            </Typography.Text>
          </Link>
          <div className="co-small">{reasonLabels(t)[item.reason]}</div>
        </div>
      )
    },
    {
      title: t("Order / customer"),
      key: "parent",
      mobile: "subtitle",
      render: (_, item) =>
        [item.parent?.label, item.client?.name, item.project?.name].filter(Boolean).join(" · ") ||
        "—"
    },
    {
      title: t("Status"),
      key: "state",
      mobile: "badge",
      width: 190,
      render: (_, item) => (
        <Space size={[4, 4]} wrap>
          <Tag color={STATE_COLORS[item.state]}>{states[item.state]}</Tag>
          {item.overdue && <Tag color="error">{t("Overdue")}</Tag>}
        </Space>
      )
    },
    {
      title: t("Answer by"),
      key: "due",
      width: 110,
      render: (_, item) =>
        ["offered", "announced"].includes(item.state) ? formatDay(item.responseDue) : ""
    },
    {
      title: t("Net"),
      key: "net",
      width: 120,
      align: "right",
      render: (_, item) => formatEuro(item.net, lng)
    }
  ];

  const isEmpty = !loading && !data.length && !searchText && filter === "all" && !parentOffer;
  const table = (
    <ResponsiveTable
      rowKey="_id"
      loading={loading}
      columns={columns}
      dataSource={data}
      mobileAvatar={false}
      onRow={record => ({ onDoubleClick: () => history.push(`/user/change-orders/${record._id}`) })}
      pagination={data.length > 50 ? { pageSize: 50 } : false}
      scroll={{ x: 760 }}
    />
  );

  return (
    <div className="change-orders-page">
      <PageToolbar
        filters={
          <Space direction="vertical" className="co-full">
            <SearchBarComp handleSearch={setSearchText} />
            <Segmented
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: t("All") },
                { value: "pending", label: t("Open") },
                { value: "overdue", label: t("Overdue") },
                { value: "approved", label: t("Approved") },
                { value: "draft", label: t("Draft") }
              ]}
            />
            {meta?.offers?.length > 0 && (
              <Select
                className="co-offer-filter"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t("All orders")}
                value={parentOffer || undefined}
                onChange={value => {
                  setParentOffer(value || "");
                  history.replace(
                    value ? `/user/change-orders?offer=${value}` : "/user/change-orders"
                  );
                }}
                options={meta.offers.map(o => ({
                  value: o._id,
                  label: [o.label, o.title, o.clientName].filter(Boolean).join(" · ")
                }))}
              />
            )}
          </Space>
        }
        actions={
          meta?.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
              {t("New change order")}
            </Button>
          )
        }
      />

      {overview && <ContractOverview overview={overview} lng={lng} />}

      {isEmpty ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>{t("No unpaid extra work")}</Typography.Title>
                <Typography.Paragraph type="secondary" className="co-empty-text">
                  {t(
                    "When something on site is not in the contract, write it down here: send the cost notice before you start, let the customer approve the price by signature or link, and the approved positions are invoiced with the main order."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
                {t("New change order")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : isMobile ? (
        table
      ) : (
        <Card styles={{ body: { padding: 0 } }}>{table}</Card>
      )}
      {!isEmpty && data.some(d => d.overdue) && filter !== "overdue" && (
        <Alert
          className="mt-3"
          type="warning"
          showIcon
          message={t("Some change orders have no answer past their date.")}
          action={
            <Button size="small" onClick={() => setFilter("overdue")}>
              {t("Show")}
            </Button>
          }
        />
      )}

      <NewChangeOrderModal
        open={newOpen}
        meta={meta}
        initialOffer={parentOffer}
        onClose={() => setNewOpen(false)}
      />
    </div>
  );
};

export default ChangeOrders;
